/**
 * Builders that turn a committed session / mini-game / migration outcome into
 * an immutable sync event for the outbox.
 *
 * Events carry only correctness + aggregate signals (XP delta, counts, earned
 * badges) — NEVER raw child answers — plus a client-generated `id` that
 * doubles as the server-side idempotency key. The shapes mirror the return
 * values of `progressStore.commitSession` / `commitMiniGame`, so wiring the
 * (dormant) emission point later is a thin adapter.
 */

import { generateId } from './identity.js';

export const EVENT_KIND = {
  SESSION: 'session',
  MINI_GAME: 'mini_game',
  MIGRATION_BASELINE: 'migration_baseline',
};

function toIso(committedAt) {
  if (typeof committedAt === 'string') return committedAt;
  const d = committedAt instanceof Date ? committedAt : new Date(committedAt ?? Date.now());
  return d.toISOString();
}

function baseEvent({ id, profileId, deviceId, kind, xpDelta, payload, committedAt, contentVersion }) {
  return {
    id: typeof id === 'string' && id.length > 0 ? id : generateId(),
    profileId: profileId ?? null,
    deviceId: deviceId ?? null,
    kind,
    xpDelta: Number.isFinite(xpDelta) ? xpDelta : 0,
    payload: payload ?? {},
    clientCommittedAt: toIso(committedAt),
    contentVersion: contentVersion ?? null,
  };
}

function summaryPayload(summary, extra) {
  return {
    correctCount: summary.correctCount ?? 0,
    totalCount: summary.totalCount ?? 0,
    perfect: Boolean(summary.perfect),
    questsCompletedDelta: summary.questsCompletedDelta ?? 0,
    newBadges: Array.isArray(summary.newBadges) ? summary.newBadges : [],
    ...extra,
  };
}

/**
 * Build a regular-session event from a `commitSession` summary.
 */
export function buildSessionEvent({
  id,
  profileId,
  deviceId,
  summary = {},
  mode = 'practice',
  grade = null,
  committedAt,
  contentVersion,
} = {}) {
  return baseEvent({
    id,
    profileId,
    deviceId,
    kind: EVENT_KIND.SESSION,
    xpDelta: summary.totalXpGained,
    payload: summaryPayload(summary, { mode, grade }),
    committedAt,
    contentVersion,
  });
}

/**
 * Build a mini-game event (dictionary game / standalone grammar rule) from a
 * `commitMiniGame` summary.
 */
export function buildMiniGameEvent({
  id,
  profileId,
  deviceId,
  summary = {},
  kind = 'grammar_rule',
  grade = null,
  committedAt,
  contentVersion,
} = {}) {
  return baseEvent({
    id,
    profileId,
    deviceId,
    kind: EVENT_KIND.MINI_GAME,
    xpDelta: summary.totalXpGained,
    payload: summaryPayload(summary, { gameKind: kind, grade }),
    committedAt,
    contentVersion,
  });
}

/**
 * Build the one-per-profile baseline event that seeds the server from legacy
 * local progress. Its id is DERIVED from the profileId, so a retry after an
 * interrupted upload reuses the same id and the server dedupes it (idempotent
 * migration). The baseline seeds the snapshot directly, so it carries no xp
 * delta.
 */
export function buildBaselineEvent({ profileId, deviceId, snapshot = {}, committedAt } = {}) {
  return baseEvent({
    id: `baseline:${profileId ?? 'unknown'}`,
    profileId,
    deviceId,
    kind: EVENT_KIND.MIGRATION_BASELINE,
    xpDelta: 0,
    payload: { snapshot },
    committedAt,
  });
}
